// Admin-side Portal Access API (Phase 3 + Phase 4).
// Internal-user authenticated endpoints under /api/portal/admin/* — used by
// the per-client "Portal Access" tab (ClientPortalAccess.jsx).
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const authHeaders = (token) => ({
  Authorization: `Bearer ${token}`,
  'Content-Type': 'application/json',
});

export const portalAdminApi = {
  // Phase 3
  listPortalUsers: (token, clientId) =>
    axios.get(`${BACKEND_URL}/api/portal/admin/clients/${clientId}/users`, { headers: authHeaders(token) }),
  patchPortalUser: (token, userId, body) =>
    axios.patch(`${BACKEND_URL}/api/portal/admin/users/${userId}`, body, { headers: authHeaders(token) }),
  getPortalUser: (token, userId) =>
    axios.get(`${BACKEND_URL}/api/portal/admin/users/${userId}`, { headers: authHeaders(token) }),
  auditLog: (token, clientId, { portalUserId, limit = 100 } = {}) => {
    const params = new URLSearchParams();
    if (portalUserId) params.set('portal_user_id', portalUserId);
    if (limit) params.set('limit', String(limit));
    const qs = params.toString() ? `?${params}` : '';
    return axios.get(`${BACKEND_URL}/api/portal/admin/clients/${clientId}/audit-log${qs}`, { headers: authHeaders(token) });
  },

  // Phase 4 — action items
  listActionItems: (token, clientId) =>
    axios.get(`${BACKEND_URL}/api/portal/admin/clients/${clientId}/action-items`, { headers: authHeaders(token) }),
  createActionItem: (token, clientId, body) =>
    axios.post(`${BACKEND_URL}/api/portal/admin/clients/${clientId}/action-items`, body, { headers: authHeaders(token) }),
  patchActionItem: (token, itemId, body) =>
    axios.patch(`${BACKEND_URL}/api/portal/admin/action-items/${itemId}`, body, { headers: authHeaders(token) }),
  deleteActionItem: (token, itemId) =>
    axios.delete(`${BACKEND_URL}/api/portal/admin/action-items/${itemId}`, { headers: authHeaders(token) }),
  nudgeActionItem: (token, itemId) =>
    axios.post(`${BACKEND_URL}/api/portal/admin/action-items/${itemId}/nudge`, {}, { headers: authHeaders(token) }),

  // Phase 4 — document requests
  listDocumentRequests: (token, clientId) =>
    axios.get(`${BACKEND_URL}/api/portal/admin/clients/${clientId}/document-requests`, { headers: authHeaders(token) }),
  createDocumentRequest: (token, clientId, body) =>
    axios.post(`${BACKEND_URL}/api/portal/admin/clients/${clientId}/document-requests`, body, { headers: authHeaders(token) }),
  patchDocumentRequest: (token, reqId, body) =>
    axios.patch(`${BACKEND_URL}/api/portal/admin/document-requests/${reqId}`, body, { headers: authHeaders(token) }),
  deleteDocumentRequest: (token, reqId) =>
    axios.delete(`${BACKEND_URL}/api/portal/admin/document-requests/${reqId}`, { headers: authHeaders(token) }),
  nudgeDocumentRequest: (token, reqId) =>
    axios.post(`${BACKEND_URL}/api/portal/admin/document-requests/${reqId}/nudge`, {}, { headers: authHeaders(token) }),
};

// Section labels shared with ClientPortalAccess.jsx so toggles stay in sync
// with backend SECTION_KEYS.
export const PRINCIPAL_SECTIONS = [
  { key: 'wealth_snapshot', label: 'Wealth Snapshot', description: 'Live portfolio total, allocation, sparkline.' },
  { key: 'action_items', label: 'Items needing input', description: 'Tasks the team has asked them to action.' },
  { key: 'document_requests', label: 'Document requests', description: 'Open requests for them to upload a document.' },
  { key: 'documents', label: 'Shared documents', description: 'Documents your team explicitly shared.' },
  { key: 'team', label: 'Your team', description: 'The internal advisers looking after the family.' },
  { key: 'whats_changed', label: "What's changed", description: 'Curated activity feed for the family.' },
  { key: 'family_entity', label: 'Family & entities', description: 'Read-only summary of family members and entities.' },
  { key: 'messages', label: 'Messages', description: 'Two-way message thread with the team.' },
];

export const ADVISER_SECTIONS = [
  { key: 'matter_scope', label: 'Matter scope', description: 'The remit you have granted the adviser.' },
  { key: 'action_items', label: 'Items needing input', description: 'Tasks routed to this adviser.' },
  { key: 'document_requests', label: 'Document requests', description: 'Documents you are asking them to supply.' },
  { key: 'documents', label: 'Shared documents', description: 'Documents shared with this adviser.' },
  { key: 'client_context', label: 'Client context', description: 'Read-only family + entity summary.' },
  { key: 'team_updates', label: 'Team updates', description: 'Curated change feed.' },
  { key: 'comments', label: 'Comments / messages', description: 'Allow them to post comments back.' },
];

export const PERMISSION_KEYS = [
  { key: 'canDownload', label: 'Download documents' },
  { key: 'canUpload', label: 'Upload documents' },
  { key: 'canComment', label: 'Post comments / messages' },
];
